import { useCallback, useState } from 'react'

import { Box, Button, Text, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import {
  CompanyStepDataDocument,
  DatevLaudsLoginDocument,
  DatevLaudsTokenStatusFragment,
  SettingsStepsLegalEntityFragment,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { WizardData } from 'modules/marketplace/lib/types'
import { Status } from 'modules/payroll_integrations_datev_lauds/lib/OauthService'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

import { DisconnectModal } from './DisconnectModal'
import { OfflineAccessModal } from './OfflineAccessModal'
import PermissionsBanner from './PermissionsBanner'

interface LegalEntityFillerProps extends WizardStepRenderParams<WizardData> {
  data: DatevWizardData
  legalEntity: SettingsStepsLegalEntityFragment
  setConsultantFilled: (value: boolean) => void
  setPayrollSoftwareFilled: (value: boolean) => void
  handleError: (legalEntityId: string, hasError: boolean) => void
}

const findCurrentToken = (
  statuses: DatevLaudsTokenStatusFragment[],
  companyId: number | null | undefined
) => {
  if (!companyId) {
    // When there is no configuration we don't have any companyId assigned.
    // In this case we return the first token.
    // It should be the unique token for the company as we don't allow creating more than a token.
    return statuses[0]
  }

  return statuses.find((token) => token?.company?.id === companyId)
}

export const LegalEntityCard = ({
  data,
  onDataChanged,
  legalEntity,
  setConsultantFilled,
  setPayrollSoftwareFilled,
  handleError,
}: LegalEntityFillerProps) => {
  const legalEntityKey = `legal_entity_${legalEntity.id}`
  const clientIdKey = `client_id_${legalEntity.id}`
  const dataAnswer = data.answers[legalEntityKey]
  const authenticatedClientId = data.answers[`authenticated_${legalEntityKey}`]
  const clientId = `${data.answers.consultant_id}-${dataAnswer}`
  const [showModal, setShowModal] = useState(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  const [bannerCode, setBannerCode] = useState(0)
  const [clientCode, setClientCode] = useState<string>(data.answers[legalEntityKey] || '')
  const consultantIdFilled = !!data.answers.consultant_id
  const payrollSoftwareFilled = !!data.answers.payroll_software

  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()

  const { data: stepData } = useQuery(DatevLaudsLoginDocument, {
    variables: {
      companyId: companyId,
      accessId: accessId,
      clientId: `${data.answers.consultant_id}-${dataAnswer}`,
    },
  })

  const [lazyStepData] = useLazyQuery(DatevLaudsLoginDocument, {
    variables: {
      companyId: companyId,
      accessId: accessId,
      clientId: `${data.answers.consultant_id}-${dataAnswer}`,
    },
  })

  const tokenStatuses = stepData?.integrationsDatevLauds.oauthTokensConnection.nodes || []
  const currentToken = findCurrentToken(tokenStatuses, null)
  const loginStatus = currentToken?.status
  const currentTokenClientId = currentToken?.clientId

  // Only fetch client data if this legal entity's client ID matches the current token's client ID
  const shouldFetchClientData =
    authenticatedClientId && authenticatedClientId === currentTokenClientId
  const { data: companyStepData } = useQuery(CompanyStepDataDocument, {
    variables: {
      companyId: companyId,
      accessId: accessId,
      clientId: String(authenticatedClientId),
      targetSystem: data.answers.payroll_software,
    },
    skip: !shouldFetchClientData,
  })

  const clientDataRequest =
    companyStepData?.integrationsDatevLauds.clientDataRequestsConnection?.nodes[0]

  const jobUuid = clientDataRequest?.jobUuid ?? ''

  const requestId = clientDataRequest?.requestId ?? ''

  const errorCode = clientDataRequest?.errorCode ?? ''

  const errorMessage = clientDataRequest?.errorMessage ?? ''

  const permissionsStatusCode =
    companyStepData?.integrationsDatevLauds?.permissionsConnection.nodes[0]?.statusCode
  const permissionNodes =
    companyStepData?.integrationsDatevLauds?.permissionsConnection?.nodes?.length

  data.answers[`job_uuid_${legalEntity.id}`] = jobUuid
  data.answers[`request_id_${legalEntity.id}`] = requestId.toString()

  useEffect(() => {
    if (loginStatus === Status.Authenticated) {
      setAuthenticated(true)
      setAuthenticatedLegalEntity()
    } else {
      setAuthenticated(false)
    }
  }, [loginStatus])

  useEffect(() => {
    if (permissionNodes && permissionNodes > 0) {
      const code = Number(permissionsStatusCode)
      setBannerCode(code)
      if (!isNaN(code)) {
        if (code === 200 && !errorCode) {
          handleError(legalEntity.id.toString(), false)
        } else {
          handleError(legalEntity.id.toString(), true)
        }
      } else {
        if (authenticated) {
          handleError(legalEntity.id.toString(), true)
        }
      }
    }
  }, [legalEntity.id, authenticated, errorCode, permissionNodes, permissionsStatusCode])

  const owner =
    currentToken?.tokenUserName || currentToken?.tokenUserEmail || currentToken?.access?.fullName
  const syncedBy = owner ? owner : i18n.t('common.unknown')
  const syncedDate = stringToDate(currentToken?.createdAt, 'backend_datetime')
    .format(`${BACKEND_DATE_FORMAT}`)
    .split('-')
    .reverse()
    .join('/')

  const onChange = (value: string) => {
    setClientCode(value)
    onLegalEntityChange(value)
  }

  const onLegalEntityChange = useCallback(
    debounce((value: string) => {
      onDataChanged({
        ...data,
        answers: {
          ...data.answers,
          [legalEntityKey]: value.toString(),
        },
      })
    }, 1000),
    [data.answers, legalEntity.id]
  )

  const setAuthenticatedLegalEntity = () => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`authenticated_${legalEntityKey}`]: String(clientId),
      },
    })
  }

  const loggedInText = () => {
    if (hasPermissions()) {
      return i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.login.offline_access_token.disconnect'
      )
    } else {
      return i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.login.offline_access_token.connect'
      )
    }
  }

  const disconnect = () => {
    setShowDisconnectModal(true)
  }

  const authenticate = async () => {
    const checkData = await lazyStepData()
    const checkTokenStatuses =
      checkData?.data?.integrationsDatevLauds.oauthTokensConnection.nodes || []
    const checkToken = findCurrentToken(checkTokenStatuses, null)

    if (checkToken) {
      disconnect()
    } else if (consultantIdFilled && payrollSoftwareFilled) {
      setShowModal(true)
    } else {
      if (!consultantIdFilled) {
        setConsultantFilled(false)
      }

      if (!payrollSoftwareFilled) {
        setPayrollSoftwareFilled(false)
      }
    }
  }

  const hasPermissions = () => {
    return authenticated && bannerCode === 200 && !errorCode
  }

  return (
    <Box
      flexDirection="column"
      justifyContent="flexStart"
      borderRadius={{ all: 'abs016' }}
      padding="s8"
      border={{ all: { width: 's1', style: 'solid', color: 'grey300' } }}
    >
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        maxWidth="full"
        width="full"
        height="full"
        padding="s8"
      >
        <Text.Normal ellipsis weight="bold">
          {legalEntity.legalName}
        </Text.Normal>

        <Button type="secondary" onClick={authenticate}>
          {loggedInText()}
        </Button>
      </Box>

      {authenticated && bannerCode === 200 && !errorCode && (
        <Box paddingX="s8">
          <Text.Normal size="200">
            {i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.login.offline_synced_by',
              {
                synced_by: syncedBy,
                synced_date: syncedDate,
                client_code: dataAnswer,
              }
            )}
          </Text.Normal>
        </Box>
      )}

      {hasPermissions() && (
        <PermissionsBanner
          statusCode={bannerCode}
          errorCode={errorCode}
          errorMessage={errorMessage}
        />
      )}

      {showModal && (
        <OfflineAccessModal
          legalEntity={legalEntity}
          data={data}
          setShowModal={setShowModal}
          onChange={onChange}
          clientCode={clientCode}
          companyId={companyId}
          onDataChanged={onDataChanged}
        />
      )}

      {showDisconnectModal && (
        <DisconnectModal
          data={data}
          legalEntityKey={legalEntityKey}
          clientIdKey={clientIdKey}
          companyId={companyId}
          accessId={accessId}
          clientId={clientId}
          setShowDisconnectModal={setShowDisconnectModal}
          setAuthenticated={setAuthenticated}
          setBannerCode={setBannerCode}
        />
      )}
    </Box>
  )
}
